"use client";

import { useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLoopPhase } from "./useLoopPhase";

/**
 * Looping application tracker board. Cards arrive on their own, then one moves Applied > Interview > Offer
 * and a follow-up reminder pops up. Phases: 0 empty, 1-3 cards arrive, 4 interview, 5 reminder, 6 offer (hold).
 */

const GREEN = "#059669";
const NAVY = "#143f80";
const GOLD = "#d69400";
const HOLDS = [900, 1100, 1100, 1100, 1900, 1900, 4200];

type Column = "Applied" | "Interview" | "Offer";
const COLUMNS: Column[] = ["Applied", "Interview", "Offer"];

const CARDS = [
  { id: "acme", role: "Operations Manager", company: "Acme Co.", arrives: 1 },
  { id: "northwind", role: "Program Lead", company: "Northwind", arrives: 2 },
  { id: "globex", role: "Business Analyst", company: "Globex", arrives: 3 },
];

function columnFor(id: string, phase: number): Column {
  if (id !== "acme") return "Applied";
  if (phase >= 6) return "Offer";
  if (phase >= 4) return "Interview";
  return "Applied";
}

export function LandingTrackerBoard() {
  const ref = useRef<HTMLDivElement>(null);
  const { phase, reduce } = useLoopPhase(ref, HOLDS);
  const spring = reduce ? { duration: 0 } : { type: "spring" as const, stiffness: 260, damping: 26 };

  const visible = CARDS.filter((c) => phase >= c.arrives);
  const stat = (col: Column) => visible.filter((c) => columnFor(c.id, phase) === col).length;

  return (
    <div
      ref={ref}
      className="relative mx-auto max-w-4xl rounded-2xl bg-white p-4 sm:p-6"
      style={{ border: "1px solid rgba(20,63,128,0.08)", boxShadow: "0 8px 40px rgba(20,63,128,0.12)" }}
      role="img"
      aria-label="The Hirecarta application tracker: new applications appear automatically and move from Applied to Interview to Offer, with follow-up reminders."
    >
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs font-extrabold uppercase" style={{ color: "#64748b", letterSpacing: "0.16em" }}>
          Your applications
        </p>
        <span className="flex items-center gap-2 text-xs font-bold" style={{ color: GREEN }}>
          <motion.span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: GREEN }}
            animate={reduce ? { opacity: 1 } : { opacity: [1, 0.3, 1] }}
            transition={reduce ? { duration: 0 } : { duration: 1.6, repeat: Infinity }}
          />
          Updates automatically
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        {COLUMNS.map((col) => (
          <div key={col} className="min-h-[250px] rounded-xl p-2 sm:p-3" style={{ background: "#f1f5f9" }}>
            <div className="mb-3 flex items-center justify-between px-1">
              <span className="text-xs font-extrabold sm:text-sm" style={{ color: NAVY }}>
                {col}
              </span>
              <motion.span
                key={stat(col)}
                className="rounded-full bg-white px-2 text-xs font-extrabold"
                style={{ color: "#475569" }}
                initial={reduce ? false : { scale: 1.5 }}
                animate={{ scale: 1 }}
                transition={spring}
              >
                {stat(col)}
              </motion.span>
            </div>
            <div className="space-y-2">
              <AnimatePresence>
                {visible
                  .filter((c) => columnFor(c.id, phase) === col)
                  .map((c) => {
                    const offer = col === "Offer";
                    return (
                      <motion.div
                        key={c.id}
                        layoutId={c.id}
                        layout={!reduce}
                        initial={reduce ? false : { opacity: 0, y: -40, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={spring}
                        className="rounded-lg bg-white p-2 sm:p-3"
                        style={{
                          border: `2px solid ${offer ? GREEN : "rgba(20,63,128,0.08)"}`,
                          boxShadow: offer ? "0 0 0 4px rgba(5,150,105,0.15)" : "0 1px 6px rgba(20,63,128,0.06)",
                        }}
                      >
                        <p className="text-xs font-extrabold leading-tight sm:text-sm" style={{ color: NAVY }}>
                          {c.role}
                        </p>
                        <p className="mt-0.5 text-xs" style={{ color: "#64748b" }}>
                          {c.company}
                        </p>
                      </motion.div>
                    );
                  })}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>

      <AnimatePresence>
        {phase === 5 && (
          <motion.div
            className="absolute -bottom-5 right-4 flex items-center gap-2 rounded-full px-4 py-2 text-xs font-extrabold text-white sm:right-8 sm:text-sm"
            style={{ background: GOLD, boxShadow: "0 6px 24px rgba(214,148,0,0.4)" }}
            initial={reduce ? false : { opacity: 0, y: 24, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12 }}
            transition={spring}
          >
            Reminder: follow up with Acme Co. on Friday
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
